import { useMemo } from "react";
import { Target } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { useAuth } from "@/lib/auth-context";
import { useGoals } from "./use-goals";
import { GoalsTable } from "./goals-table";
import { NewGoalDialog } from "./new-goal-dialog";

// Phase 7 sub-bucket 18 — SPA port of /app/admin/goals/page.tsx. The
// per-goal target count comes from the goal row itself in the worker
// (post-port, the API returns a flat list) — for v1 we fetch the count
// implicitly by not surfacing it (showing 0 / count via a follow-up
// query). Practical compromise: omit the live target count from the
// list page; the detail page exposes it accurately.

export function GoalsListPage() {
  const session = useAuth().session;
  const canManage = !!session?.permissions.includes("goals.manage");
  const query = useGoals({ includeArchived: true });
  const rows = useMemo(() => query.data ?? [], [query.data]);

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Goals
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Quarterly, yearly or one-off goals with live previews against
            partner data.
          </p>
        </div>
        {canManage && <NewGoalDialog />}
      </header>

      {query.isLoading ? (
        <Skeleton className="h-64 w-full rounded-xl" />
      ) : rows.length > 0 ? (
        <GoalsTable rows={rows} targetCounts={new Map()} canManage={canManage} />
      ) : (
        <EmptyState
          icon={Target}
          title="No goals yet"
          description="Create a goal to start tracking partner outcomes — deals closed, ARR generated, journey tasks completed."
          action={canManage ? <NewGoalDialog variant="empty" /> : undefined}
        />
      )}
    </div>
  );
}
